import type { PurchaseProviderName, PurchaseResponse, StoreProduct } from '../schemas.js';

export type PurchaseStatus = 'granted' | 'pending' | 'failed' | 'cancelled' | 'blocked';

export interface PurchaseOptions {
  /** MO-06: the player confirmed the one-time "over $100 today" prompt; sent to the API as `confirm` */
  confirmedOverLimit?: boolean;
}

/** The request field that carries the MO-06 confirmation (DevPurchase / CheckoutRequest / PurchaseVerify `confirm`). */
export function confirmFlag(opts: PurchaseOptions): { confirm?: true } {
  return opts.confirmedOverLimit === true ? { confirm: true } : {};
}

export interface PurchaseResult {
  status: PurchaseStatus;
  provider: PurchaseProviderName | 'none';
  productId: string;
  /** provider receipt / transaction / session id, when there is one */
  receipt?: string;
  reason?: string;
  /** the API's answer (granted contents + fresh snapshot) when it was reached */
  response?: PurchaseResponse;
}

/** A store product with the price string to show (MO-03.1: always real money). */
export interface IapProduct extends StoreProduct {
  displayPrice: string | null;
}

export interface IapProvider {
  readonly provider: PurchaseProviderName | 'none';
  products(): Promise<IapProduct[]>;
  purchase(productId: string, opts?: PurchaseOptions): Promise<PurchaseResult>;
  restore(): Promise<PurchaseResult[]>;
  /** Finish a purchase interrupted by a redirect or app restart (Stripe return URL, pending native transactions). */
  resume(): Promise<PurchaseResult | null>;
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

export function withUsdPrice(p: StoreProduct): IapProduct {
  return { ...p, displayPrice: p.priceUsdCents === null ? null : usd.format(p.priceUsdCents / 100) };
}

export function fromResponse(provider: PurchaseProviderName, productId: string, res: PurchaseResponse, receipt?: string): PurchaseResult {
  return {
    status: res.status,
    provider,
    productId,
    ...(receipt !== undefined ? { receipt } : {}),
    ...(res.reason !== undefined ? { reason: res.reason } : {}),
    response: res,
  };
}

/** `iap=none` (Discord build, store switched off). */
export class NoIapProvider implements IapProvider {
  readonly provider = 'none';
  async products(): Promise<IapProduct[]> {
    return [];
  }
  async purchase(productId: string): Promise<PurchaseResult> {
    return { status: 'blocked', provider: this.provider, productId, reason: 'unavailable' };
  }
  async restore(): Promise<PurchaseResult[]> {
    return [];
  }
  async resume(): Promise<PurchaseResult | null> {
    return null;
  }
}
